import React, { useMemo } from 'react';
import { IonLabel, IonText } from '@ionic/react';
import { createOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import type { ZohoItem } from '@rsmaids/shared/types/zoho';
import SheetModal from './SheetModal';

interface ItemPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  items: ZohoItem[];
  /** Whether `item` matches what was typed in the search box (each form keeps its own matching rules). */
  matches: (item: ZohoItem, query: string) => boolean;
  formatRate: (rate: number) => string;
  onSelect: (item: ZohoItem) => void;
  /** Adds a header button for typing an item that is not in the catalog. */
  onCustomItem?: () => void;
}

/**
 * A bottom sheet to pick a Zoho catalog item for a line item: name + rate rows and a search box. Always rendered by
 * the host and toggled by `isOpen`, so Ionic runs its present / dismiss transitions.
 *
 * The catalog has ~1,900 items, so only the first 20 render while browsing and the first 100 while searching.
 */
const ItemPickerSheet: React.FC<ItemPickerSheetProps> = ({ isOpen, onClose, items, matches, formatRate, onSelect, onCustomItem }) => {
  const { t } = useTranslation();

  // One string per item (the sheet's list works on strings), plus the way back to the item
  const { rows, byRow } = useMemo(() => {
    const map = new Map<string, ZohoItem>();
    for (const item of items) map.set(`${item.name} | ${item.item_id}`, item);
    return { rows: [...map.keys()], byRow: map };
  }, [items]);

  return (
    <SheetModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('components.ItemPickerSheet.title')}
      menuOptions={[]}
      headerAction={onCustomItem ? { icon: createOutline, label: t('components.ItemPickerSheet.customItem'), onClick: onCustomItem } : undefined}
      isSelector
      selectorItems={rows}
      searchPlaceholder={t('components.ItemPickerSheet.searchPlaceholder')}
      selectorEmptyText={t('components.ItemPickerSheet.noItems')}
      filterSelectorItem={(row, term) => {
        const item = byRow.get(row);
        return !item || !term.trim() || matches(item, term);
      }}
      selectorResultCap={(searching) => (searching ? 100 : 20)}
      onSelectorItemClick={(row) => {
        const item = byRow.get(row);
        if (item) onSelect(item);
      }}
      renderSelectorItem={(row) => {
        const item = byRow.get(row);
        return (
          <>
            <IonLabel className="ion-text-wrap">{item?.name ?? row.split(' | ')[0]}</IonLabel>
            {item && item.rate > 0 && (
              <IonText slot="end" color="primary" style={{ fontSize: '0.92rem', fontWeight: 600, flexShrink: 0, marginInlineStart: 20 }}>
                {formatRate(item.rate)}
              </IonText>
            )}
          </>
        );
      }}
    />
  );
};

export default ItemPickerSheet;
